import type { BottomSheetHandle } from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { MorphemeMnemonicsMeaningViewContextProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._context";
import { MorphemeMnemonicsMeaningViewTabLandscapeView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning.__views.landscape";
import { MorphemeMnemonicsMeaningViewTabPortraitView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning.__views.portrait";
import { MorphemeMnemonicsMeaningExamplesSheet } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._sections.compounds";
import { useCallback, useEffect, useRef, useState } from "react";

/** A bottom sheet that mounts on its first open and unmounts again once dismissed. */
function useLazyBottomSheet() {
  const ref = useRef<BottomSheetHandle>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isMounted) {
      ref.current?.open();
    }
  }, [isMounted]);

  const open = useCallback(() => {
    if (ref.current) {
      ref.current.open();
    } else {
      setIsMounted(true);
    }
  }, []);
  const onDismiss = useCallback(() => setIsMounted(false), []);

  return { isMounted, onDismiss, open, ref };
}

/** Scrollable mnemonic meaning tab (title, decomposition, hero glyph beside the mnemonic, scroll hints, edit affordance). */
export function MorphemeMnemonicsMeaningViewTab({
  extraBottomPadding = 0,
  showEditButton = true,
}: {
  extraBottomPadding?: number;
  showEditButton?: boolean;
}) {
  const [orientation] = useContextSelectors(RootContext, (c) => c.orientation);
  const [grapheme, graphemeId, mnemonicTerms] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
    (c) => c.readonlySlice.data.graphemeId,
    (c) => c.readonlySlice.data.mnemonicTerms,
  );
  const compoundsSheet = useLazyBottomSheet();
  const mnemonicUsagesSheet = useLazyBottomSheet();

  return (
    <MorphemeMnemonicsMeaningViewContextProvider
      openCompoundsSheet={compoundsSheet.open}
      openMnemonicUsagesSheet={mnemonicUsagesSheet.open}
    >
      {orientation === "landscape" ? (
        <MorphemeMnemonicsMeaningViewTabLandscapeView
          extraBottomPadding={extraBottomPadding}
          showEditButton={showEditButton}
        />
      ) : (
        <MorphemeMnemonicsMeaningViewTabPortraitView
          extraBottomPadding={extraBottomPadding}
          showEditButton={showEditButton}
        />
      )}
      {compoundsSheet.isMounted ? (
        <MorphemeMnemonicsMeaningExamplesSheet
          initialView={{ grapheme, graphemeId, kind: "compounds" }}
          onDismiss={compoundsSheet.onDismiss}
          ref={compoundsSheet.ref}
        />
      ) : null}
      {mnemonicUsagesSheet.isMounted ? (
        <MorphemeMnemonicsMeaningExamplesSheet
          initialView={{ grapheme, kind: "mnemonic-usages", mnemonicTerms }}
          onDismiss={mnemonicUsagesSheet.onDismiss}
          ref={mnemonicUsagesSheet.ref}
        />
      ) : null}
    </MorphemeMnemonicsMeaningViewContextProvider>
  );
}
